import { useContext, useState } from 'react';
import { Button, DialogActions, Dialog, DialogTitle, IconButton, DialogContent, Alert, CircularProgress, Box } from '@mui/material';
import { UserContext } from '../../../../../components/shared/useUser';
import useNswagClient from '../../../../../hooks/api/useNswagClient';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import { useTranslation } from 'react-i18next';
import { exportFile } from '../../../../../utils';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const DoNotCountItemsBulkAddItemsModal = (props: { isItemsModalOpen: boolean, handleClose: () => void, reloadData: () => void, hasExistingItems: boolean }) => {
  const { t } = useTranslation('doNotCountItems');
  const { selectedStore } = useContext(UserContext);
  const { updateDoNotCountItemsFromReport, checkDoNotCountFileHasData, getDoNotCountExport } = useNswagClient();
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isProccessed, setIsProccessed] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [processedItems, setProcessedItems] = useState(0);
  const [failedItems, setFailedItems] = useState(0);
  const [failedDetails, setFailedDetails] = useState<string[]>([]);
  const [failedMessage, setFailedMessage] = useState<string[]>([]);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [showEmptyFileConfirmation, setShowEmptyFileConfirmation] = useState(false);

  const handleDownloadTemplateFile = () => {
    setIsDownloading(true);
    getDoNotCountExport(selectedStore?.storeNumber ?? '-1')
      .then((data) => {
        exportFile(data.data, data.headers?.['content-type'], data.fileName ?? '');
      })
      .catch(() => {
        alert(t('downloadErrorMessage'));
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };
  

  // Function to read the file as a Base64 string
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result?.toString().split(',')[1] ?? '');
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsProccessed(false);
    const file = event.target.files?.[0] || null;

    if (!file) {
      return;
    }

    if (props.hasExistingItems) {
      const content = await fileToBase64(file);
      const payload = { name: file.name, content };
      const hasData = await checkDoNotCountFileHasData(payload).catch(() => true);
      if (!hasData) {
        setPendingFile(file);
        setShowEmptyFileConfirmation(true);
        return;
      }
    }

    await uploadFile(file);
  };

  const handleConfirmEmptyFileUpload = async () => {
    setShowEmptyFileConfirmation(false);
    if (pendingFile) {
      await uploadFile(pendingFile);
      setPendingFile(null);
    }
  };

  const handleCancelEmptyFileUpload = () => {
    setShowEmptyFileConfirmation(false);
    setPendingFile(null);
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    fileInput.value = '';
  };

  const uploadFile = async (file: File) => {
    const content = await fileToBase64(file); // Convert file to Base64
    const payload = {
      name: file.name,
      content: content,
    };

    setIsLoading(true);
    updateDoNotCountItemsFromReport(selectedStore?.storeNumber ?? '-1', payload)
      .then((result) => {
        setIsSuccess(true);
        setProcessedItems(result.updatedItems ?? 0);
        setFailedItems(result.failedItems ?? 0);
        setFailedDetails(result.failedDetails ?? []);
        props.reloadData();
      })
      .catch((e) => {
        setIsSuccess(false);
        setFailedMessage(Array.isArray(e) ? e : []);
      })
      .finally(() => {
        setIsLoading(false);
        setIsProccessed(true);
        const fileInput = document.getElementById('fileInput') as HTMLInputElement;
        fileInput.value = '';
      });
  };

  const close = () => {
    if (!isLoading) {
      isSuccess && props.reloadData();
      props.handleClose();
      setIsProccessed(false);
      setProcessedItems(0);
      setFailedItems(0);
      setFailedDetails([]);
      setPendingFile(null);
      setShowEmptyFileConfirmation(false);
    }
  };

  return (
    <Dialog open={props.isItemsModalOpen}
      onClose={close}
      maxWidth='sm'
      fullWidth>
      <DialogTitle>
        {t('bulkAddItem')}
        <IconButton disabled={isLoading}
          onClick={close}
          sx={{
            position: 'absolute',
            right: 16,
            top: 8,
            color: (theme) => theme.palette.custom.gray[800],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {isLoading && <Alert variant='filled'
          severity='info'>
          {t('processingMessage')}
        </Alert>}
        {isProccessed && isSuccess && failedItems == 0 && <Alert variant='filled'
          severity='success'>
          {t('successMessage', { processedItems })}
        </Alert>}
        {isProccessed && isSuccess && failedItems > 0 && <Alert variant='filled'
          severity='warning'>
          {t('partialSuccessMessage', { processedItems, failedItems })}
          {failedDetails.length > 0 && <ul>
            {failedDetails.map((reason, index) => (
              <li key={`${index}-${reason}`}>{reason}</li>
            ))}
          </ul>}
        </Alert>}
        {isProccessed && !isSuccess && 
        (failedMessage.length > 0 ? <Alert variant='filled'
          severity='error'>
          {t('errorMessage')}
          <ul>
            {failedMessage.map((item, index) => (
              <li key={`${index}-${item}`}>{item}</li>
            ))}
          </ul>
        </Alert> :
          <Alert variant='filled'
            severity='error'>
            {t('unknownErrorProcessingMessage')}
          </Alert>)}
      </DialogContent>
      <DialogActions >
        <Button onClick={handleDownloadTemplateFile}
          disabled={isDownloading}
          startIcon={isDownloading ? <CircularProgress size={20}
            sx={{ ml: '-4px', mr: '4px' }} /> : <DownloadIcon />}
          size='lg'>
          {t('downloadTemplateFile')}
        </Button>
        <Button disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20}
            sx={{ ml: '-4px', mr: '4px' }} /> : <UploadIcon />}
          size='lg'
          onClick={() => document.getElementById('fileInput')?.click()}
        >
          {t('uploadFile')}
          <input
            id="fileInput"
            type="file"
            accept=".xlsx"
            hidden
            onChange={handleFileSelect}
          />
        </Button>
      </DialogActions>
      <Dialog
        open={showEmptyFileConfirmation}
        onClose={handleCancelEmptyFileUpload}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningAmberIcon color='warning' />
            {t('emptyFileConfirmationTitle')}
          </Box>
          <IconButton
            onClick={handleCancelEmptyFileUpload}
            sx={{
              position: 'absolute',
              right: 16,
              top: 8,
              color: (theme) => theme.palette.custom.gray[800],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Alert variant='filled'
            severity='warning'>
            {t('emptyFileConfirmationMessage')}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ gap: 1 }}>
          <Button
            size='lg'
            onClick={handleCancelEmptyFileUpload}>
            {t('cancel')}
          </Button>
          <Button
            size='lg'
            onClick={handleConfirmEmptyFileUpload}>
            {t('confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default DoNotCountItemsBulkAddItemsModal;